import { catalogAmount, formatUsd } from '@/lib/service-catalog'

export const WORKIZ_SYMPTOMS = [
  {
    title: 'Leads and jobs counted twice',
    detail: 'because a converted lead lives on both sides of the export',
  },
  {
    title: 'Cancelled work counted as revenue',
    detail: 'so ROAS looks better than it is',
  },
  {
    title: '"Website" absorbing every channel',
    detail: 'an ad click, a search result and a repeat customer all land in the same bucket',
  },
  {
    title: 'Repeat customers treated as a marketing source',
    detail: 'showing infinite return and making every paid channel look worse',
  },
  {
    title: 'Source names split three ways',
    detail: 'so one channel reports as three',
  },
  {
    title: 'Ad spend that never meets the revenue it produced',
    detail: 'because it lives in a platform and the revenue lives in your CRM',
  },
] as const

export const WORKIZ_BUILD_PILLARS = [
  {
    title: 'One place where spend meets revenue',
    body: 'Workiz jobs and leads sync automatically every night. Google Ads and Local Services spend pulls in on its own. Facebook, Thumbtack, Angi and anything else gets added alongside. All of it lands against the same source names, on the same dates, in the same table.',
    note: "That's the part nobody does by hand well, and it's the part that makes everything below possible.",
  },
  {
    title: 'Attribution you can act on',
    items: [
      { label: 'Cost per lead and booked job', detail: 'by channel' },
      { label: 'ROAS on collected revenue', detail: 'not work later cancelled' },
      { label: 'Full funnel by source', detail: 'opportunities through closed' },
      { label: 'Drop-off by channel', detail: 'lead quality vs closing' },
      { label: 'Repeat business credited', detail: 'back to the original channel' },
    ],
  },
  {
    title: 'A dashboard, not a spreadsheet',
    body: 'Any date range. Month over month. A shareable link that works on a phone. No exports, no pasting, no rebuilding.',
  },
  {
    title: 'Numbers that hold up',
    body: "Every status mapped to a funnel stage you've signed off on. Cancelled work excluded from revenue. Spam and duplicates filtered. Source names standardized and locked to internal IDs, so renaming something in Workiz never splits your history. Anything the system doesn't recognise gets flagged rather than silently dropped.",
  },
] as const

export type WorkizFeature = {
  label: string
  detail?: string
}

export type WorkizStage = {
  n: string
  title: string
  claim: string
  claimEm: string
  items: WorkizFeature[]
  practice: string
  extras?: 'channels'
}

export const WORKIZ_STAGES: WorkizStage[] = [
  {
    n: '01',
    title: 'Audit',
    claim: 'We look at your account and ',
    claimEm: "tell you what's actually there.",
    items: [
      { label: 'Lead source review', detail: 'set, applied, and drifting' },
      { label: 'Unattributed work', detail: 'how much, and where it is credited' },
      { label: 'Ad platform check', detail: 'what can connect' },
      { label: 'Funnel by channel', detail: 'real data, with drop-off' },
      { label: 'Reporting gaps', detail: 'where return is over- or under-stated' },
      { label: 'Fixed fee', detail: 'credited against a build' },
    ],
    practice: 'Written findings with your numbers, and a clear yes or no on a build.',
  },
  {
    n: '02',
    title: 'Build',
    claim: 'Spend and revenue land in ',
    claimEm: 'the same table, every night.',
    items: [
      { label: 'Nightly Workiz sync' },
      { label: 'Ad spend connected' },
      { label: 'Shareable dashboard', detail: 'any date range' },
      { label: 'Findings document' },
      { label: 'Reconciliation', detail: 'against a period you already reported' },
    ],
    extras: 'channels',
    practice: 'Two weeks. We walk through every difference from your old numbers first.',
  },
  {
    n: '03',
    title: 'Ongoing',
    claim: 'Reporting setups do not stay accurate ',
    claimEm: 'on their own.',
    items: [
      { label: 'Sync monitoring', detail: 'failures fixed before they hit a report' },
      { label: 'Mappings kept current', detail: 'as statuses and sources change' },
      { label: 'Platform changes', detail: 'Workiz or Google' },
      { label: 'Accuracy checks' },
      { label: 'Dashboard updates' },
      { label: 'Direct access', detail: 'no new project quote for questions' },
    ],
    practice: 'APIs, statuses, and source names keep moving. This keeps the numbers trustworthy.',
  },
]

export const WORKIZ_CHANNELS: { category: string; tools: { name: string; domain: string | null }[] }[] = [
  {
    category: 'Field service',
    tools: [{ name: 'Workiz', domain: 'workiz.com' }],
  },
  {
    category: 'Ads & marketplaces',
    tools: [
      { name: 'Google Ads', domain: 'ads.google.com' },
      { name: 'Google Local Services', domain: 'ads.google.com' },
      { name: 'Facebook', domain: 'facebook.com' },
      { name: 'Thumbtack', domain: 'thumbtack.com' },
      { name: 'Angi', domain: 'angi.com' },
      { name: 'Yelp', domain: 'yelp.com' },
    ],
  },
]

export const WORKIZ_PRICING = {
  title: 'Pricing',
  subtitle: 'Start with the audit, or go straight to the full build. Audit fee is credited if you continue.',
} as const

export type WorkizPaidPlan = {
  id: 'audit' | 'build'
  productId: 'workiz-audit' | 'workiz-reporting'
  slug: 'workiz'
  name: string
  tagline: string
  price: number
  priceDisplay: string
  priceNote: string
  cta: string
  highlight: boolean
  badge?: string
  bestFor: string
  features: WorkizFeature[]
  turnaround: string
}

export const WORKIZ_PAID_PLANS: WorkizPaidPlan[] = [
  {
    id: 'audit',
    productId: 'workiz-audit',
    slug: 'workiz',
    name: 'Workiz Marketing Audit',
    tagline: 'Find out what your numbers are actually telling you',
    price: catalogAmount('workiz-audit'),
    priceDisplay: formatUsd(catalogAmount('workiz-audit')),
    priceNote: 'One-time · credited against a build if you continue',
    cta: 'Book the audit',
    highlight: false,
    bestFor: 'Know what is fixable before you commit to a build.',
    features: [
      { label: 'Lead source review', detail: 'set, applied, and drifting' },
      { label: 'Cost per booked job', detail: 'real return by channel' },
      { label: 'Funnel by source', detail: 'opportunities through closed, plus drop-off' },
      { label: 'Unattributed work', detail: 'how much, and where it is credited' },
      { label: 'Ad platform check', detail: 'what can connect' },
      { label: 'Written findings', detail: 'recommendations you can use without us' },
      { label: 'Walkthrough call' },
    ],
    turnaround: 'About one week',
  },
  {
    id: 'build',
    productId: 'workiz-reporting',
    slug: 'workiz',
    name: 'Reporting & Attribution Build',
    tagline: 'Spend and revenue in one place, updating on its own',
    price: catalogAmount('workiz-build'),
    priceDisplay: formatUsd(catalogAmount('workiz-build')),
    priceNote: 'One-time · includes the audit',
    cta: 'Book Services',
    highlight: true,
    badge: 'Most popular',
    bestFor: 'See which channels deserve more budget next month.',
    features: [
      { label: 'Everything in the audit' },
      { label: 'Nightly Workiz sync', detail: 'no CSV exports' },
      { label: 'Ad spend connected', detail: 'Google Ads, LSA, and other channels' },
      { label: 'Cost per job and ROAS', detail: 'on money actually collected' },
      { label: 'Shareable dashboard', detail: 'any date range, works on a phone' },
      { label: 'Status and source mapping', detail: 'locked so renaming does not split history' },
      { label: 'Handover and 14 days of support' },
    ],
    turnaround: 'Two weeks from kickoff',
  },
]

export const WORKIZ_ONGOING_PLAN = {
  id: 'ongoing',
  name: 'Ongoing Management',
  tagline: 'Keep it accurate as everything around it changes',
  priceDisplay: 'Get in touch',
  priceNote: 'Monthly · no commitment, cancel any time',
  cta: 'Get In Touch',
  bestFor: 'The numbers stay right without you owning a developer for it.',
  features: [
    { label: 'Sync monitoring', detail: 'failures fixed before they hit a report' },
    { label: 'Mappings kept current' },
    { label: 'Platform changes', detail: 'Workiz or Google' },
    { label: 'Accuracy checks' },
    { label: 'Dashboard updates' },
    { label: 'Direct access', detail: 'no new project quote for questions' },
  ],
} as const

export const WORKIZ_ADDONS = {
  title: 'Also available',
  note: 'Quoted on request, during or after a build.',
  items: [
    {
      name: 'Weekly automated delivery',
      description: 'The report in your inbox every Monday.',
    },
    {
      name: 'Lifetime channel attribution',
      description: 'Credit repeat jobs back to the channel that originally won the customer.',
    },
    {
      name: 'Intake attribution',
      description: 'Call tracking and form capture, so source does not depend on whoever answers.',
    },
    {
      name: 'Offline conversion import',
      description: 'Send booked and closed jobs back to Google Ads.',
    },
  ],
} as const

export const WORKIZ_FAQS = [
  {
    question: "What's the difference between the audit and the build?",
    answer:
      "The audit tells you what your numbers actually say and what's fixable. The build makes it happen automatically and keeps it that way. If you take the build, the audit is included - you're not paying twice.",
  },
  {
    question: 'Do I need the Workiz Developer add-on?',
    answer:
      "Yes, for both the audit and the build. It's what allows me to read your data directly rather than working from exports. You enable it in Settings → Integrations → Developer and generate a key - takes about a minute.",
  },
  {
    question: 'Will my numbers change?',
    answer:
      'Almost certainly. Once double-counting is removed and cancelled work is excluded from revenue, some channels look better than you thought and some look worse. We document every difference and walk you through it before you see the new report cold.',
  },
  {
    question: 'Can all my ad spend be automated?',
    answer:
      'Google Ads and Local Services, yes. Facebook needs a paid data connector, usually $20-40 a month. Some lead marketplaces have no API at all and will always be entered by hand. We tell you which is which before you buy anything.',
  },
  {
    question: 'Who owns it at the end?',
    answer:
      'You do. Everything is built inside your own Google account. No password sharing, no extra paid seats, and nothing stops working if we part ways.',
  },
] as const
